import { Chip, CircularProgress, TextField } from "@material-ui/core";
import {
  getComponentIcon,
  getComponentName,
} from "../../Assignments/ComponentTypes";
import {
  AssignmentDTO,
  ComponentModelDTO,
  ComponentOperationType,
  ComponentType,
  DatePickerRangeDTO,
  GroopitFilterComponentDisplayDTO,
} from "groopit-js/client";
import React, {
  Fragment,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from "react";
import { ComponentOperationPicker } from "./ComponentOperationPicker";
import { DatePicker } from "../DatePicker";
import useLatestRef from "../../react/react-use/useLatestRef";
import { useGroopitApiContext } from "groopit-js";
import { useDebounce } from "react-use";
import {
  createFilterRemoveComponentAction,
  createFilterUpdateComponentAction,
} from "../State/FilterActions";
import { StyledAutoComplete } from "../../styles/MaterialGroopTheme";
import { ColorPopTextField } from "../../Controls/ColorPopTextField";
import { useFilterContext } from "../State/FilterContext";
import { useFilterDispatchContext } from "../State/FilterDispatchContext";

const getOperationTypes = (component: ComponentModelDTO) => {
  const operationTypes = [
    ComponentOperationType.Any,
    ComponentOperationType.None,
  ];
  switch (component.type) {
    case ComponentType.Note:
    case ComponentType.SaveToSalesforce:
      operationTypes.push(ComponentOperationType.Contains);
      break;
    case ComponentType.FixedDecimal:
      operationTypes.push(
        ComponentOperationType.Equal,
        ComponentOperationType.LessThan,
        ComponentOperationType.GreaterThan,
        ComponentOperationType.LessThanOrEqual,
        ComponentOperationType.GreaterThanOrEqual,
      );
      break;
    case ComponentType.DateTime:
      operationTypes.push(ComponentOperationType.Equal);
      break;
    case ComponentType.FollowUpRequest:
      operationTypes.push(
        ComponentOperationType.FollowUpComplete,
        ComponentOperationType.FollowUpIncomplete,
      );
      break;
    case ComponentType.MultipleChoice:
    case ComponentType.MultipleChoiceExtended:
      operationTypes.push(ComponentOperationType.AnyOf);
      if (component.multiple) {
        operationTypes.push(ComponentOperationType.AllOf);
      }
      break;
  }
  return operationTypes;
};

// All components default to "Any" but if a user sets a value then
// the operation should switch to something meaningful for that value
// (since "Any" and "None" don't have values)
const getDefaultValueOperationType = (
  component: ComponentModelDTO,
  op: ComponentOperationType,
) => {
  if (op !== ComponentOperationType.Any && op !== ComponentOperationType.None) {
    return op;
  }

  switch (component.type) {
    case ComponentType.Note:
    case ComponentType.SaveToSalesforce:
      return ComponentOperationType.Contains;
    case ComponentType.FixedDecimal:
      return ComponentOperationType.Equal;
    case ComponentType.DateTime:
      return ComponentOperationType.Equal;
    case ComponentType.MultipleChoice:
    case ComponentType.MultipleChoiceExtended:
      return ComponentOperationType.AnyOf;
    default:
      // The other types don't actually have values
      // so they really shouldn't hit this code path
      return ComponentOperationType.Any;
  }
};

export const ComponentFilter = (props: {
  assignment: AssignmentDTO;
  component: ComponentModelDTO;
}) => {
  const { assignment, component } = props;
  const { filter } = useFilterContext();

  const assignmentFilter = filter.filter.assignments.find(
    (a) => a.id === assignment.id,
  );
  const componentFilter =
    assignmentFilter?.components?.find(
      (c) => c.componentModelId === component.componentModel_Id,
    ) ??
    new GroopitFilterComponentDisplayDTO({
      componentModelId: component.componentModel_Id,
      componentModelType: component.type,
      componentModelDescription: component.description ?? "",
      operation: ComponentOperationType.Any,
    });
  const childComponents = assignment.components.filter(
    (c) => c.parentModelComponentModel_Id === component.componentModel_Id,
  );
  const childComponentFilters = childComponents.map((childComponent) => {
    return (
      assignmentFilter?.components?.find(
        (c) => c.componentModelId === childComponent.componentModel_Id,
      ) ??
      new GroopitFilterComponentDisplayDTO({
        componentModelId: childComponent.componentModel_Id,
        componentModelType: childComponent.type,
        componentModelDescription: childComponent.description ?? "",
        operation: ComponentOperationType.Any,
      })
    );
  });

  const getComponentFilter = () => {
    switch (component.type) {
      case ComponentType.Note:
        return (
          <NoteComponentFilter
            assignment={assignment}
            component={component}
            filter={componentFilter}
          />
        );
      case ComponentType.MultipleChoice:
        return (
          <TagComponentFilter
            assignment={assignment}
            component={component}
            filter={componentFilter}
          />
        );
      case ComponentType.DateTime:
        return (
          <DateTimeComponentFilter
            assignment={assignment}
            component={component}
            filter={componentFilter}
          />
        );
      case ComponentType.FixedDecimal:
        return (
          <NumberComponentFilter
            assignment={assignment}
            component={component}
            filter={componentFilter}
          />
        );
      case ComponentType.SaveToSalesforce:
        return (
          <SaveToSalesforceComponentFilter
            assignment={assignment}
            component={component}
            filter={componentFilter}
          />
        );
      case ComponentType.MultipleChoiceExtended:
        return (
          <MultipleChoiceExtendedComponentFilter
            assignment={assignment}
            component={component}
            filter={componentFilter}
          />
        );
      default:
        return null;
    }
  };

  if (component.type === ComponentType.Table) {
    return (
      <TableComponentFilter
        assignment={assignment}
        component={component}
        childComponents={childComponents}
        childFilters={childComponentFilters}
      />
    );
  } else {
    return (
      <StandardComponentFilter
        assignment={assignment}
        component={component}
        filter={componentFilter}
      >
        {getComponentFilter()}
      </StandardComponentFilter>
    );
  }
};

interface ComponentFilterProps {
  assignment: AssignmentDTO;
  component: ComponentModelDTO;
  filter: GroopitFilterComponentDisplayDTO;
}

const StandardComponentFilter = (
  props: PropsWithChildren<ComponentFilterProps>,
) => {
  const { assignment, component, filter, children } = props;
  const { filterDispatcher } = useFilterDispatchContext();

  const componentIcon = useMemo(
    () => getComponentIcon(component.type),
    [component.type],
  );

  const componentName = useMemo(
    () => getComponentName(component.type),
    [component.type],
  );

  const operationTypes = useMemo(
    () => getOperationTypes(component),
    [component],
  );

  return (
    <div>
      <div className="p-4 space-y-2">
        {component.hiddenFromMembers && (
          <div className="text-display-caption text-color-accent px-1">
            Data will only be visible to the group organizer.
          </div>
        )}
        <div className="flex gap-2">
          <div className="w-8 h-8 p-1 flex-shrink-0">
            {componentIcon && (
              <img
                src={componentIcon}
                alt={componentName}
                className="w-full h-full"
              />
            )}
          </div>
          <div className="flex-grow space-y-2">
            <div className="text-normal pt-1">{component.description}</div>
            <div className="text-sm">
              Include when value is{" "}
              <ComponentOperationPicker
                operationTypes={operationTypes}
                operation={filter.operation}
                onOperationChanged={(op) =>
                  filterDispatcher(
                    createFilterUpdateComponentAction(
                      filter.componentModelId,
                      assignment,
                      op,
                      filter.value,
                    ),
                  )
                }
              />
            </div>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

const NoteComponentFilter = (props: ComponentFilterProps) => {
  const { assignment, component, filter } = props;
  const { filterDispatcher } = useFilterDispatchContext();

  return (
    <ColorPopTextField
      className="w-full"
      highlighted={filter.value}
      value={filter.value ?? ""}
      onChange={(e) =>
        filterDispatcher(
          createFilterUpdateComponentAction(
            filter.componentModelId,
            assignment,
            getDefaultValueOperationType(component, filter.operation),
            e.target.value,
          ),
        )
      }
    />
  );
};

const TagComponentFilter = (props: ComponentFilterProps) => {
  const { assignment, component, filter } = props;
  const { filterDispatcher } = useFilterDispatchContext();

  const options = useMemo(
    () => component.options?.split("|") || [],
    [component.options],
  );
  const choices = useMemo(
    () => (filter.value ? (filter.value as string[]) : []),
    [filter.value],
  );

  const toggleTagSelection = useCallback(
    (option: string, selected: boolean) => {
      if (selected) {
        filterDispatcher(
          createFilterUpdateComponentAction(
            filter.componentModelId,
            assignment,
            getDefaultValueOperationType(component, filter.operation),
            choices.filter((c) => c !== option),
          ),
        );
      } else {
        filterDispatcher(
          createFilterUpdateComponentAction(
            filter.componentModelId,
            assignment,
            getDefaultValueOperationType(component, filter.operation),
            [...choices, option],
          ),
        );
      }
    },
    [
      assignment,
      choices,
      component,
      filter.componentModelId,
      filter.operation,
      filterDispatcher,
    ],
  );

  return (
    <ul className="tag-list p-0 list-none">
      {options.map((option) => {
        const selected = choices.some((c) => c === option);
        return (
          <li
            key={option}
            className={`btn btn-tag text-break-words ${
              selected ? "active" : ""
            } cursor-pointer`}
            onClick={() => toggleTagSelection(option, selected)}
          >
            {option}
          </li>
        );
      })}
    </ul>
  );
};

const DateTimeComponentFilter = (props: ComponentFilterProps) => {
  const { assignment, component, filter } = props;
  const { filterDispatcher } = useFilterDispatchContext();

  const onDateSelected = useCallback(
    (date?: DatePickerRangeDTO) => {
      if (date) {
        filterDispatcher(
          createFilterUpdateComponentAction(
            filter.componentModelId,
            assignment,
            getDefaultValueOperationType(component, filter.operation),
            date,
          ),
        );
      } else {
        filterDispatcher(
          createFilterRemoveComponentAction(
            filter.componentModelId,
            assignment.id,
          ),
        );
      }
    },
    [assignment, component, filter, filterDispatcher],
  );

  return (
    <DatePicker
      variant={filter.value ? "contained" : "outlined"}
      selectedOption={
        filter.value ? (filter.value as DatePickerRangeDTO) : undefined
      }
      onChange={onDateSelected}
    />
  );
};

const NumberComponentFilter = (props: ComponentFilterProps) => {
  const { assignment, component, filter } = props;
  const { filterDispatcher } = useFilterDispatchContext();

  const isPercent = useMemo(
    () => component.options?.startsWith("P"),
    [component.options],
  );
  const places = useMemo(() => {
    return Number.parseInt(component.options?.slice(1) ?? "0");
  }, [component.options]);

  return (
    <ColorPopTextField
      className="w-full"
      highlighted={filter.value !== undefined}
      value={
        filter.value !== undefined
          ? isPercent
            ? filter.value * 100
            : filter.value
          : ""
      }
      type="number"
      InputProps={{
        inputProps: {
          min: 0,
          step: 1 / Math.pow(10, places),
        },
      }}
      onChange={(e) =>
        filterDispatcher(
          createFilterUpdateComponentAction(
            filter.componentModelId,
            assignment,
            getDefaultValueOperationType(component, filter.operation),
            isPercent
              ? Number.parseFloat(e.target.value) / 100
              : Number.parseFloat(e.target.value),
          ),
        )
      }
    />
  );
};

const SaveToSalesforceComponentFilter = (props: ComponentFilterProps) => {
  const { assignment, component, filter } = props;
  const { filterDispatcher } = useFilterDispatchContext();

  return (
    <ColorPopTextField
      className="w-full"
      highlighted={filter.value}
      value={filter.value ?? ""}
      onChange={(e) =>
        filterDispatcher(
          createFilterUpdateComponentAction(
            filter.componentModelId,
            assignment,
            getDefaultValueOperationType(component, filter.operation),
            e.target.value,
          ),
        )
      }
    />
  );
};

const MultipleChoiceExtendedComponentFilter = (props: ComponentFilterProps) => {
  const { assignment, component, filter } = props;
  const api = useLatestRef(useGroopitApiContext());
  const { filterDispatcher } = useFilterDispatchContext();

  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [availableOptions, setAvailableOptions] = useState<Set<string>>(
    new Set(),
  );
  const [cachedOriginalAvailableOptions, setCachedOriginalAvailableOptions] =
    useState<Set<string>>(new Set());

  const loadOptions = useCallback(
    async (query: string) => {
      setLoading(true);

      try {
        const options = await api.current.assignment.getComponentOptions(
          assignment.id,
          component.componentModel_Id,
          query,
          undefined,
          100,
        );
        setAvailableOptions(new Set<string>(options));
        if (!query && !cachedOriginalAvailableOptions.size) {
          setCachedOriginalAvailableOptions(new Set<string>(options));
        }
      } finally {
        setLoading(false);
      }
    },
    [
      api,
      assignment.id,
      cachedOriginalAvailableOptions.size,
      component.componentModel_Id,
    ],
  );

  useDebounce(
    () => {
      if (!query) {
        setAvailableOptions(cachedOriginalAvailableOptions);
        return;
      }

      loadOptions(query);
    },
    500,
    [loadOptions, query],
  );

  return (
    <StyledAutoComplete
      className="mt-2"
      multiple
      freeSolo
      loading={loading}
      onOpen={() => !cachedOriginalAvailableOptions.size && loadOptions("")}
      forcePopupIcon={true}
      clearOnBlur={false}
      filterOptions={(options) => options}
      options={Array.from(availableOptions)}
      inputValue={query}
      onInputChange={(e, value) => setQuery(value)}
      value={useMemo(
        () => (filter.value ? (filter.value as string[]) : []),
        [filter.value],
      )}
      onChange={(_e, options) => {
        filterDispatcher(
          createFilterUpdateComponentAction(
            filter.componentModelId,
            assignment,
            getDefaultValueOperationType(component, filter.operation),
            options,
          ),
        );
      }}
      renderInput={(params) => (
        <>
          <div></div>
          <TextField
            {...params}
            label="Type to find items"
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        </>
      )}
      renderTags={(values, getTagProps) =>
        values.map((value, index) => (
          <Chip
            key={index}
            {...getTagProps({ index })}
            className="max-w-56 truncate"
            color="primary"
            label={value}
          />
        ))
      }
    />
  );
};

interface TableComponentFilterProps {
  assignment: AssignmentDTO;
  component: ComponentModelDTO;
  childComponents: ComponentModelDTO[];
  childFilters: GroopitFilterComponentDisplayDTO[];
}

const TableComponentFilter = (props: TableComponentFilterProps) => {
  const { assignment, component, childComponents, childFilters } = props;
  if (!childComponents || !childFilters) {
    throw new Error(
      "childComponents and childFilters must be provided to tables",
    );
  }
  const { filterDispatcher } = useFilterDispatchContext();

  const componentIcon = useMemo(
    () => getComponentIcon(component.type),
    [component.type],
  );

  const componentName = useMemo(
    () => getComponentName(component.type),
    [component.type],
  );

  const getChildComponentFilter = useCallback(
    (i: number) => {
      switch (childComponents[i].type) {
        case ComponentType.Note:
          return (
            <NoteComponentFilter
              {...props}
              component={childComponents[i]}
              filter={childFilters[i]}
            />
          );
        case ComponentType.FixedDecimal:
          return (
            <NumberComponentFilter
              {...props}
              component={childComponents[i]}
              filter={childFilters[i]}
            />
          );
        default:
          throw new Error(
            `Unsupported child of table: ${childComponents[i].type}`,
          );
      }
    },
    [childComponents, childFilters, props],
  );

  return (
    <div>
      <div className="flex gap-2 p-4">
        <div className="w-8 h-8 p-1 flex-shrink-0">
          {componentIcon && (
            <img
              src={componentIcon}
              alt={componentName}
              className="w-full h-full"
            />
          )}
        </div>
        <div className="flex-grow space-y-2">
          <div className="text-l pt-1">{component.description}</div>
          <div className="grid grid-cols-3">
            {childComponents.map((component, i) => (
              <Fragment key={component.componentModel_Id}>
                <div className="border border-app-gray-08 bg-app-gray-accent-11">
                  {component.description}
                </div>
                <div className="border border-app-gray-08">
                  {getChildComponentFilter(i)}
                </div>
                <div className="border border-app-gray-08 bg-app-teal-x-light">
                  <ComponentOperationPicker
                    operationTypes={getOperationTypes(component)}
                    operation={childFilters[i].operation}
                    onOperationChanged={(op) => {
                      filterDispatcher(
                        createFilterUpdateComponentAction(
                          childFilters[i].componentModelId,
                          assignment,
                          op,
                          childFilters[i].value,
                        ),
                      );
                    }}
                  />
                </div>
              </Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
